/**
 * Homepage showcase behavior — mounted once against the index template's
 * <main data-ph-showcase>, not per-section. Each direct child is a
 * Shopify-injected `.shopify-section` wrapper around whatever section type
 * the merchant has placed there (landing hero, chapter, story, signup,
 * shop-edit, video carousel); this script only cares about the shared
 * `[data-ph-panel]` / `[data-header-tone]` markers inside each one, so it
 * works across every section type without each of them re-implementing the
 * scroll mechanism.
 *
 * The nav and header controls (`[data-ph-header]`,
 * `[data-ph-showcase-nav]`, `[data-ph-showcase-current]`,
 * `[data-ph-showcase-prev]`, `[data-ph-showcase-next]`) live in the header
 * section, outside the showcase root, so they are looked up from the
 * document and every one of them is optional.
 *
 * Ported from apps/shared/full-bleed-app.js's IntersectionObserver +
 * wheel handling.
 */
(function () {
  let cleanup = null;

  function mountShowcase(root) {
    const controller = new AbortController();
    const { signal } = controller;

    const panels = Array.from(root.querySelectorAll('[data-ph-panel]'));
    const nav = document.querySelector('[data-ph-showcase-nav]');
    const currentLabel = document.querySelector('[data-ph-showcase-current]');
    const prevBtn = document.querySelector('[data-ph-showcase-prev]');
    const nextBtn = document.querySelector('[data-ph-showcase-next]');
    const header = document.querySelector('[data-ph-header]');
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!panels.length) return () => controller.abort();

    let activeIndex = -1;

    function toneOf(panel) {
      return panel.dataset.headerTone || 'light';
    }

    function labelOf(panel) {
      const heading = panel.querySelector('.ph-chapter__pillar, [data-ph-panel-label]');
      return heading ? heading.textContent : panel.getAttribute('aria-label') || '';
    }

    function setActive(index) {
      if (index < 0 || index >= panels.length || index === activeIndex) return;
      activeIndex = index;
      const panel = panels[index];

      if (header) header.dataset.tone = toneOf(panel);
      if (currentLabel) currentLabel.textContent = labelOf(panel);

      // Nav state contract with sections/header.liquid: arrows disable at
      // each end and the track fill reads --ph-showcase-progress (0–1).
      if (prevBtn) prevBtn.disabled = index === 0;
      if (nextBtn) nextBtn.disabled = index === panels.length - 1;
      if (nav) {
        const progress = panels.length > 1 ? index / (panels.length - 1) : 0;
        (nav.querySelector('.ph-showcase-nav__track') || nav).style.setProperty(
          '--ph-showcase-progress',
          progress.toFixed(3)
        );
      }
      // Video play/pause is handled independently by its own per-video
      // IntersectionObserver below — every video plays whenever it's in
      // view, not just the single "active" (closest-to-center) panel's, so
      // more than one can legitimately be playing at once during a scroll
      // transition. See that observer for why.
    }

    /*
     * Panels are as wide as their own media's aspect ratio demands, so a
     * wide panel in a narrow window can never reach a fixed 0.6 ratio.
     * Tracking every panel's ratio and activating the most-visible one
     * keeps the same "closest to centre wins" behavior as the prototype
     * without ever stalling on an oversized panel.
     */
    const ratios = new Map(panels.map((panel) => [panel, 0]));

    /*
     * The wordmark's reveal (`.is-landing-section`) used to be driven by the
     * same "closest to centre" swap as `activeIndex` above, which meant it
     * flipped the instant the next panel edged ahead in visible area — on a
     * mobile scroll, while the landing chapter was still a large fraction
     * of the screen. The prototype waits until the landing chapter has
     * actually scrolled away on mobile, so this reads the landing panel's
     * own intersection ratio directly instead, gated to mobile specifically
     * (desktop's timing already matches the prototype as-is).
     */
    const landingPanel = panels.find((panel) => panel.querySelector('[data-ph-landing]'));
    const landingCompactQuery = matchMedia('(max-width: 989px)');

    function updateLandingSection() {
      if (!header) return;
      // header.liquid starts the homepage in landing mode; with no Landing
      // hero on the page there's nothing to hand over to, so show the wordmark.
      if (!landingPanel) {
        header.classList.remove('is-landing-section');
        return;
      }
      if (!landingCompactQuery.matches) {
        header.classList.toggle('is-landing-section', panels[activeIndex] === landingPanel);
        return;
      }
      // Read live geometry directly, the same way the parallax/tease/signup
      // scroll handlers further down do, instead of the chapter-nav
      // IntersectionObserver's `ratios` map above: that observer's
      // threshold steps are tuned for "closest to centre" chapter
      // switching, not for reliably catching the landing panel's ratio
      // once it's scrolled all the way past on mobile.
      const rootRect = root.getBoundingClientRect();
      const panelRect = landingPanel.getBoundingClientRect();
      const visible = Math.min(panelRect.bottom, rootRect.bottom) - Math.max(panelRect.top, rootRect.top);
      const landingRatio = panelRect.height > 0 ? Math.max(0, visible) / panelRect.height : 0;
      header.classList.toggle('is-landing-section', landingRatio > 0.02);
    }

    let landingSectionRaf = 0;
    function requestLandingSectionUpdate() {
      if (landingSectionRaf) return;
      landingSectionRaf = requestAnimationFrame(() => {
        landingSectionRaf = 0;
        updateLandingSection();
      });
    }
    if (landingPanel) {
      root.addEventListener('scroll', requestLandingSectionUpdate, { signal, passive: true });
    }

    /*
     * A repeating "nudge" on the landing panel — client request to make it
     * more obvious the page scrolls, since a full-bleed opening screen with
     * no visible chrome otherwise gives no hint. The rail eases out and
     * settles back, a physical cue rather than a static icon, and it loops
     * back-to-back with no pause between — not just once — since a single
     * nudge turned out to be too subtle to reliably catch a first-time
     * visitor's eye. A faster multi-cycle "shake" (out-back-out-back within
     * one burst) was tried first and never read as smooth no matter how it
     * was tuned; one slow, gentle out-and-back arc, immediately repeating,
     * reads far calmer — each arc starts and ends at velocity zero, so
     * chaining them has no seam to feel jerky at. It's cancelled for good
     * the instant the visitor does anything that could be a real scroll
     * (wheel, touch, a drag, or the rail actually moving) so it never
     * fights or repeats on top of them.
     *
     * Runs on both desktop (nudges scrollLeft) and mobile (nudges
     * scrollTop) — `nudgeAxisQuery` below just picks which axis, the same
     * plain width check (not touch-based) used elsewhere for this, see
     * header.js's COMPACT_QUERY for why.
     */
    if (landingPanel && !reducedMotion) {
      const nudgeAxisQuery = matchMedia('(max-width: 989px)');
      let nudgeCancelled = false;
      const cancelNudge = () => {
        nudgeCancelled = true;
      };
      root.addEventListener('wheel', cancelNudge, { signal, once: true, passive: true });
      root.addEventListener('touchstart', cancelNudge, { signal, once: true, passive: true });
      root.addEventListener('pointerdown', cancelNudge, { signal, once: true });
      signal.addEventListener('abort', cancelNudge);

      // One continuous out-and-back arc — a single sine hump, so it starts
      // and ends at velocity zero (no abrupt start/stop) and has only one
      // peak, unlike the earlier multi-cycle version where each cycle
      // could swing a different distance. Slow and gentle by design.
      // Tweak these two to test variations.
      const WIGGLE_AMPLITUDE = 50;
      const WIGGLE_DURATION_MS = 1800;

      function wiggleBurst() {
        return new Promise((resolve) => {
          const vertical = nudgeAxisQuery.matches;
          const base = vertical ? root.scrollTop : root.scrollLeft;
          const start = performance.now();
          function frame(now) {
            if (nudgeCancelled) return resolve();
            const t = Math.min(1, (now - start) / WIGGLE_DURATION_MS);
            const value = base + WIGGLE_AMPLITUDE * Math.sin(Math.PI * t);
            if (vertical) root.scrollTop = value;
            else root.scrollLeft = value;
            if (t < 1) requestAnimationFrame(frame);
            else {
              if (vertical) root.scrollTop = base;
              else root.scrollLeft = base;
              resolve();
            }
          }
          requestAnimationFrame(frame);
        });
      }

      (async () => {
        await new Promise((resolve) => setTimeout(resolve, 1400));
        while (!nudgeCancelled) {
          // Anything already moved the rail (a real scroll, or the visitor
          // navigated straight past landing) — the hint is no longer needed.
          if (root.scrollLeft > 0 || root.scrollTop > 0) return;
          await wiggleBurst();
        }
      })();
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => ratios.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0));

        let best = -1;
        let bestRatio = 0;
        panels.forEach((panel, index) => {
          const ratio = ratios.get(panel) || 0;
          if (ratio > bestRatio) {
            bestRatio = ratio;
            best = index;
          }
        });
        if (best >= 0) setActive(best);
        updateLandingSection();
      },
      // A couple of extra low-end thresholds (beyond the 0.25 steps already
      // needed for the "closest to centre" comparison above) so the mobile
      // landing-ratio check above fires promptly as it nears zero, instead
      // of only updating at the same coarse 0/0.25 steps.
      { root, threshold: [0, 0.02, 0.05, 0.1, 0.25, 0.5, 0.6, 0.75, 1] }
    );
    panels.forEach((panel) => observer.observe(panel));

    // Fade media in once it can actually paint. `readyState >= 2`
    // (HAVE_CURRENT_DATA) is video's equivalent of `img.complete` — without
    // this check a video whose 'loadeddata' already fired before this ran
    // (a small/cached file, or one warmed by the prefetch observer below)
    // would sit at `data-loaded` unset — i.e. invisible — forever, since
    // `{ once: true }` only catches events that fire *after* it attaches.
    panels.forEach((panel) => {
      const media = panel.querySelector('img, video');
      if (!media) return;
      if (media.tagName === 'IMG') {
        if (media.complete) media.dataset.loaded = 'true';
        else
          media.addEventListener(
            'load',
            () => {
              media.dataset.loaded = 'true';
            },
            { once: true, signal }
          );
      } else if (media.readyState >= 2) {
        media.dataset.loaded = 'true';
      } else {
        media.addEventListener(
          'loadeddata',
          () => {
            media.dataset.loaded = 'true';
          },
          { once: true, signal }
        );
      }
    });

    // Warm upcoming panels' video well before they're reached, matching the
    // prototype's own look-ahead media hydration — otherwise a video only
    // starts fetching once it's actually in view (the play observer below
    // is what flips `preload` from 'none' to 'auto' today), so it sits
    // blank for as long as that fetch takes right as it scrolls into view.
    // A generous root-relative margin here means "several panels' worth of
    // scroll away", not a fixed pixel distance, so it scales with viewport
    // size the same way the panels themselves do. All four sides are
    // extended (not just left/right) because the rail switches from
    // horizontal to a vertical column on mobile (see the `max-width: 989px`
    // block in component-homepage-showcase.css) — a margin that only
    // extended sideways gave mobile zero look-ahead, so a panel's video
    // never started loading until it was already on screen, leaving it
    // blank for the whole fetch.
    const prefetchObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const video = entry.target.querySelector('video');
          if (video && video.preload !== 'auto') video.preload = 'auto';
          prefetchObserver.unobserve(entry.target);
        });
      },
      { root, rootMargin: '300% 300% 300% 300%', threshold: 0 }
    );
    panels.forEach((panel) => prefetchObserver.observe(panel));
    signal.addEventListener('abort', () => prefetchObserver.disconnect());

    /*
     * Muted in-view playback, ported from the prototype's own observer
     * (apps/shared/full-bleed-app.js): every video plays or pauses purely
     * on its *own* visibility, independent of every other video — not
     * gated to a single "closest to center" panel. During a scroll
     * transition two adjacent panels are often both partially in view at
     * once, so their videos legitimately play together; this is the
     * correct, intended behavior, not a bug to fix toward "only one at a
     * time". `cinemaVideo` (set by toggleCinema() below) is exempted from
     * the intersection check entirely, the same way the prototype exempts
     * it — once a video is enlarged it's moved to a fixed, full-viewport
     * position and must keep playing regardless of what the observer
     * (still watching its *original* slot) thinks its visibility is.
     */
    let cinemaVideo = null;
    const videos = Array.from(root.querySelectorAll('video'));
    // The landing hero's video is exempted from this observer: it's
    // `position: fixed` (see sections/landing-hero.liquid), not a normal
    // flow descendant of `root`, and IntersectionObserver computing
    // intersection for a fixed-position target against a *custom* scrolling
    // root (rather than the default document viewport) is exactly the
    // combination browsers get wrong — it was intermittently reporting the
    // always-on-screen landing video as non-intersecting and pausing it for
    // good. It already autoplays and loops on its own native attributes, so
    // it doesn't need this observer's help to begin with.
    const inViewObservedVideos = videos.filter((video) => !video.closest('[data-ph-landing]'));
    if (inViewObservedVideos.length) {
      const videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            // The video carousel (assets/video-carousel.js) has its own, separate
            // "enlarge with sound" cinema system that this observer knows nothing
            // about — `cinemaVideo` above only ever tracks this file's own
            // toggleCinema(). Without also checking the carousel's own `.is-selected`
            // card, this observer would pause a carousel video the instant its own
            // component re-ran its playback sync, even mid-cinema-playback.
            if (video === cinemaVideo || video.closest('.ph-vc__card.is-selected')) return;
            if (entry.isIntersecting && !reducedMotion) {
              if (video.preload === 'none') video.preload = 'auto';
              video.play().catch(() => {});
            } else {
              video.pause();
            }
          });
        },
        { root, threshold: 0 }
      );
      inViewObservedVideos.forEach((video) => videoObserver.observe(video));
      signal.addEventListener('abort', () => videoObserver.disconnect());
    }

    /*
     * Click-to-enlarge ("cinema mode"), ported from the prototype's own
     * toggleCinema()/exitCinema() — moves the clicked video to a
     * fixed, full-viewport position with sound, and puts it back exactly
     * where it came from on close. Only videos with no link get this
     * (sections/story.liquid and sections/chapter.liquid both skip
     * rendering the click-to-enlarge affordance's *alternative* — a link —
     * so `video.closest('a')` is a reliable, purely structural way to tell
     * the two apart without a separate data attribute).
     */
    const cinemaEligible = videos.filter((video) => !video.closest('a'));
    if (cinemaEligible.length) {
      let cinemaHome = null;
      const cinemaClose = document.createElement('button');
      cinemaClose.type = 'button';
      cinemaClose.className = 'ph-cinema-close';
      cinemaClose.textContent = 'Close';
      cinemaClose.hidden = true;
      document.body.append(cinemaClose);

      function exitCinema() {
        if (!cinemaVideo) return;
        const video = cinemaVideo;
        video.muted = true;
        video.removeAttribute('aria-pressed');
        video.setAttribute('aria-label', 'Play larger with sound');
        video.classList.remove('ph-cinema-video');
        if (cinemaHome) cinemaHome.parent.insertBefore(video, cinemaHome.next);
        document.body.classList.remove('ph-cinema-mode');
        cinemaClose.hidden = true;
        cinemaVideo = null;
        cinemaHome = null;
        if (document.activeElement === cinemaClose) video.focus({ preventScroll: true });
      }

      function toggleCinema(video) {
        if (cinemaVideo === video) {
          exitCinema();
          return;
        }
        exitCinema();
        cinemaVideo = video;
        cinemaHome = { parent: video.parentElement, next: video.nextSibling };
        video.muted = false;
        video.volume = 1;
        video.setAttribute('aria-pressed', 'true');
        video.setAttribute('aria-label', 'Exit and mute video');
        video.classList.add('ph-cinema-video');
        document.body.append(video);
        document.body.classList.add('ph-cinema-mode');
        cinemaClose.hidden = false;
        video.play().catch(() => {});
        requestAnimationFrame(() => cinemaClose.focus({ preventScroll: true }));
      }

      cinemaClose.addEventListener(
        'click',
        (event) => {
          event.stopPropagation();
          exitCinema();
        },
        { signal }
      );
      cinemaEligible.forEach((video) => {
        video.tabIndex = 0;
        video.setAttribute('role', 'button');
        video.setAttribute('aria-label', 'Play larger with sound');
        video.addEventListener('click', () => toggleCinema(video), { signal });
        video.addEventListener(
          'keydown',
          (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            toggleCinema(video);
          },
          { signal }
        );
      });
      document.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Escape') exitCinema();
        },
        { signal }
      );
      signal.addEventListener('abort', () => {
        exitCinema();
        cinemaClose.remove();
      });
    }

    function scrollToIndex(index) {
      const clamped = Math.max(0, Math.min(panels.length - 1, index));
      const target = panels[clamped];

      // The landing-panel "nudge" below cancels itself on a real wheel/
      // touch/pointerdown *on this rail* — but the header nav links that
      // call scrollToIndex live outside `root` entirely (the header isn't
      // inside `<main data-ph-showcase>`), so clicking one never fires any
      // of those on `root` and the nudge just keeps overwriting scrollLeft
      // every frame, fighting this call indefinitely (confirmed live: even
      // a direct `root.scrollLeft = ...` got silently reset back to ~0
      // while a wiggle was in flight). A synthetic pointerdown on `root`
      // reuses that same cancel path so any programmatic scroll reliably
      // wins, without needing a real gesture first.
      root.dispatchEvent(new Event('pointerdown', { bubbles: true }));

      // Native `loading="lazy"` only starts fetching an image once it
      // nears the viewport. Jumping straight to a panel several chapters
      // away (via a nav click, or the location.hash handler below) skips
      // right past every panel between here and there in one go — those
      // images never actually intersect the viewport, so they never start
      // loading at all (confirmed live: waiting on their own `load` event
      // did nothing, because nothing had triggered them to begin with).
      // Forcing them to load now, in parallel, is what actually gets their
      // real height established, which is what the corrective re-scroll
      // below needs something to converge on.
      const precedingImages = panels
        .slice(0, clamped)
        .flatMap((panel) => Array.from(panel.querySelectorAll('img[loading="lazy"]')));

      for (const img of precedingImages) {
        img.loading = 'eager';
      }

      target.scrollIntoView({
        behavior: reducedMotion ? 'auto' : 'smooth',
        inline: 'start',
        block: 'start',
      });

      const stillLoading = precedingImages.filter((img) => !img.complete);
      if (!stillLoading.length) return;

      const settleController = new AbortController();
      signal.addEventListener('abort', () => settleController.abort());
      // Hard cap so a pathologically slow or broken image can't leave
      // this listening forever.
      window.setTimeout(() => settleController.abort(), 15000);

      let resettleRaf = 0;
      const requestResettle = () => {
        if (resettleRaf) return;
        resettleRaf = requestAnimationFrame(() => {
          resettleRaf = 0;
          target.scrollIntoView({ behavior: 'auto', inline: 'start', block: 'start' });
        });
      };

      for (const img of stillLoading) {
        img.addEventListener('load', requestResettle, { signal: settleController.signal });
        img.addEventListener('error', requestResettle, { signal: settleController.signal });
      }
    }

    if (prevBtn) prevBtn.addEventListener('click', () => scrollToIndex(activeIndex - 1), { signal });
    if (nextBtn) nextBtn.addEventListener('click', () => scrollToIndex(activeIndex + 1), { signal });

    // The header wordmark normally links to the store root — on the homepage itself
    // that's a same-page, full-reload no-op, so scroll back to the landing panel
    // instead. This only runs at all when `[data-ph-showcase]` exists (the homepage),
    // so it never affects the logo's normal home-navigation behavior on other pages.
    const logoLink = document.querySelector('[data-ph-logo-link]');
    if (logoLink) {
      logoLink.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          scrollToIndex(0);
        },
        { signal }
      );
    }

    // A panel can be targeted by its element id, or by the merchant-set
    // "Anchor ID" (`data-ph-anchor`) — sections keep `section.id` as their real
    // id because other code looks panels up by it (e.g. the signup reminder).
    const panelIndexForHash = (hash) => {
      const id = decodeURIComponent((hash || '').replace(/^#/, ''));
      if (!id) return -1;
      return panels.findIndex((panel) => panel.id === id || panel.dataset.phAnchor === id);
    };

    // Header quick-nav links (and any other link to `#id`, `/#id` or this page's
    // full URL + `#id`) scroll within this rail instead of the browser's native
    // anchor jump — a plain fragment navigation only scrolls the *document*, and
    // this rail is its own scroll container, so the native jump would do nothing
    // here. Delegated, so links rendered after mount (drawers, CTAs) work too.
    document.addEventListener(
      'click',
      (event) => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
        const link = event.target instanceof Element ? event.target.closest('a[href*="#"]') : null;
        if (!link || link.target === '_blank') return;
        const url = new URL(link.href, location.href);
        if (url.origin !== location.origin || url.pathname !== location.pathname) return;
        const index = panelIndexForHash(url.hash);
        if (index === -1) return;
        event.preventDefault();
        scrollToIndex(index);
      },
      { signal }
    );

    // A hash set some other way (typed into the address bar, back/forward).
    window.addEventListener(
      'hashchange',
      () => {
        const index = panelIndexForHash(location.hash);
        if (index !== -1) scrollToIndex(index);
      },
      { signal }
    );

    // Arriving here via a cross-page anchor nav click (a header quick-nav
    // link clicked from some other page — snippets/ph-nav-href.liquid
    // normalizes those to `/#id` so the browser actually navigates here)
    // lands with the hash already in the URL, but nothing above runs for
    // that: the click already happened on the previous page, and the
    // browser's own native fragment-scroll can't reach this rail either
    // (same reason the click-listener above exists — it only scrolls the
    // *document*, not this horizontal scroll container). Handle that
    // initial hash once, on mount.
    if (location.hash) {
      const initialIndex = panelIndexForHash(location.hash);
      if (initialIndex !== -1) scrollToIndex(initialIndex);
    }

    // Translate vertical wheel/trackpad motion into horizontal scroll on
    // desktop. Skipped on the mobile/touch breakpoint (CSS already
    // switches to vertical scroll there) and skipped entirely under
    // reduced motion, since hijacking the wheel to force horizontal
    // motion works against that preference.
    const desktopQuery = matchMedia('(min-width: 801px) and (hover: hover) and (pointer: fine)');
    let wheelDelta = 0;
    let wheelRaf = 0;
    if (!reducedMotion) {
      root.addEventListener(
        'wheel',
        (event) => {
          if (!desktopQuery.matches) return;
          event.preventDefault();
          wheelDelta += event.deltaX + event.deltaY;
          if (wheelRaf) return;
          wheelRaf = requestAnimationFrame(() => {
            root.scrollBy({ left: wheelDelta, behavior: 'auto' });
            wheelDelta = 0;
            wheelRaf = 0;
          });
        },
        { signal, passive: false }
      );
    }

    // Scroll-linked parallax on each panel's own media, ported from
    // full-bleed-app.js's `measurePanels()`/`updateScrollUi()` (the
    // `parallaxLayers` overscan-and-pan effect — this is the "image scales
    // as it comes into view" behaviour from the prototype). The landing
    // hero's own media is deliberately excluded, matching the prototype
    // (`!item.closest('.video-landing')`) — it already has the particle
    // canvas/poster treatment and doesn't need a second effect competing
    // with it.
    if (!reducedMotion) {
      const verticalQuery = matchMedia('(max-width: 989px)');
      // The opening-teaser's own media gets a completely different,
      // dedicated scale treatment (see updateOpeningTeaser() below and
      // component-chapter.css's `[data-ph-opening-teaser] .ph-chapter__media`
      // rule) that would otherwise fight this one over the same `transform`
      // property.
      const parallaxItems = Array.from(root.querySelectorAll('.ph-chapter__media, .ph-story-item__media')).filter(
        (item) => !item.closest('[data-ph-opening-teaser]')
      );
      let parallaxRaf = 0;

      // Geometry (panel/media size, scroll position) is read fresh on every
      // call rather than cached, so this is correct regardless of when
      // lazy-loaded images/videos actually finish decoding, resize, or the
      // theme editor swaps sections in and out — there is no separate
      // "measure once, apply many times" step to go stale.
      function applyParallax() {
        parallaxRaf = 0;
        const vertical = verticalQuery.matches;
        const position = vertical ? root.scrollTop : root.scrollLeft;
        const viewportSize = vertical ? root.clientHeight : root.clientWidth;
        const viewportCenter = position + viewportSize / 2;
        const scale = vertical ? 1.035 : 1.12;

        parallaxItems.forEach((item) => {
          // Matches the prototype's own filter
          // (`item instanceof HTMLVideoElement || width > height`) — only
          // landscape stills and video get the overscan/pan; a portrait
          // editorial shot is left alone rather than panned oddly.
          const isVideo = item.tagName === 'VIDEO';
          const naturalWidth = Number(item.getAttribute('width')) || item.videoWidth || item.naturalWidth;
          const naturalHeight = Number(item.getAttribute('height')) || item.videoHeight || item.naturalHeight;
          if (!isVideo && naturalWidth && naturalHeight && naturalWidth <= naturalHeight) {
            item.style.removeProperty('transform');
            return;
          }

          const panel = item.closest('[data-ph-panel]');
          if (!panel) return;
          const size = vertical ? panel.offsetHeight : panel.offsetWidth;
          const mediaSize = vertical ? item.offsetHeight : item.offsetWidth;
          if (!size || !mediaSize) return;

          const center = vertical ? panel.offsetTop + size / 2 : panel.offsetLeft + size / 2;
          const range = Math.max((viewportSize + size) / 2, 1);
          const maxOffset = Math.max(0, (mediaSize * scale - size) / 2 - 1);
          const progress = Math.max(-1, Math.min(1, (viewportCenter - center) / range));
          const offset = progress * maxOffset;

          item.style.transform = vertical
            ? `translate3d(0, ${offset}px, 0) scaleY(${scale})`
            : `translate3d(${offset}px, 0, 0) scaleX(${scale})`;
        });
      }

      function requestParallax() {
        if (parallaxRaf) return;
        parallaxRaf = requestAnimationFrame(applyParallax);
      }

      if (parallaxItems.length) {
        applyParallax();
        root.addEventListener('scroll', requestParallax, { signal, passive: true });
        window.addEventListener('resize', requestParallax, { signal });
        // Lazy media reports a 0 offsetWidth/Height until it has actually
        // loaded — one more pass once each item is ready covers that
        // without needing a separate re-measure path.
        parallaxItems.forEach((item) => {
          const loadEvent = item.tagName === 'IMG' ? 'load' : 'loadeddata';
          item.addEventListener(loadEvent, requestParallax, { once: true, signal });
        });
        signal.addEventListener('abort', () => {
          if (parallaxRaf) cancelAnimationFrame(parallaxRaf);
          parallaxItems.forEach((item) => item.style.removeProperty('transform'));
        });
      }
    }

    // The landing hero's video/logo/canvas live in a `position: fixed` layer
    // (see sections/landing-hero.liquid) so they hold in place instead of
    // scrolling away with the rail. Per the prototype's own CSS, it never
    // fades or toggles visibility at all — it stays fully opaque forever
    // and is simply *covered*, as you scroll, by whichever later panel
    // currently occupies the viewport (every panel besides landing itself
    // gets a higher stacking order — see component-homepage-showcase.css).
    // An opacity-based fade was tried here and reverted: with the stacking
    // fixed, it only produced a redundant, unwanted cross-fade on top of
    // the coverage that was already happening.
    // (`landingPanel` itself is declared once, further up, alongside the
    // header's `.is-landing-section` handling — both this block and that
    // one need it.)

    // The "opening teaser" — the depth-parallax chapter right after the
    // landing hero — scales in on a completely different basis than a
    // story's tease/continue card: ported verbatim from the prototype's
    // apps/full-bleed/src/main.js `updateTeaser()`, it's a pure function of
    // scroll distance from the very start (not this panel's own on-screen
    // ratio), eased with a cubic in-out curve, and it only ever *increases*
    // while scrolling forward — reaching full scale and staying there even
    // once the panel has scrolled past and out of view. It only reduces
    // again if you scroll back toward the start. Reduced motion pins it at
    // full scale immediately, matching the source's `reduced ? 1 : ...`.
    //
    // The panel itself is already a flat 100vw (component-chapter.css's
    // `[data-ph-opening-teaser]` rule, matching the prototype's own
    // `.video-landing + .slide { width: 100vw !important; margin-left: ... }`
    // exactly) — this only scales the *media inside* from 68% to a flat
    // 100%, same as the source.
    const openingTeaser = landingPanel && root.querySelector('[data-ph-opening-teaser]');
    if (openingTeaser && landingPanel) {
      if (reducedMotion) {
        openingTeaser.style.setProperty('--ph-opening-teaser-scale', '1');
      } else {
        const verticalQuery = matchMedia('(max-width: 989px)');
        let openingTeaserRaf = 0;

        function updateOpeningTeaser() {
          openingTeaserRaf = 0;
          const vertical = verticalQuery.matches;
          const position = vertical ? root.scrollTop : root.scrollLeft;
          const landingSize = vertical ? landingPanel.offsetHeight : landingPanel.offsetWidth;
          const progress = Math.min(1, position / Math.max(1, landingSize * 0.45));
          const eased = progress < 0.5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2;
          const initialScale = vertical ? 0.86 : 0.68;
          openingTeaser.style.setProperty(
            '--ph-opening-teaser-scale',
            (initialScale + (1 - initialScale) * eased).toFixed(4)
          );
        }

        function requestOpeningTeaser() {
          if (openingTeaserRaf) return;
          openingTeaserRaf = requestAnimationFrame(updateOpeningTeaser);
        }

        updateOpeningTeaser();
        root.addEventListener('scroll', requestOpeningTeaser, { signal, passive: true });
        window.addEventListener('resize', requestOpeningTeaser, { signal });
        verticalQuery.addEventListener('change', requestOpeningTeaser, { signal });
        signal.addEventListener('abort', () => {
          if (openingTeaserRaf) cancelAnimationFrame(openingTeaserRaf);
        });
      }
    }

    // Tease panels (first item of a story, styled as a "continue" card)
    // scale up smoothly as they enter view, driven by how much of the
    // panel is actually visible rather than a fixed on/off state.
    const teaseEls = Array.from(root.querySelectorAll('[data-ph-tease]'));
    if (teaseEls.length && !reducedMotion) {
      const teaseObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const scale = 0.68 + entry.intersectionRatio * 0.32;
            entry.target.style.setProperty('--ph-tease-scale', Math.min(1, scale).toFixed(3));
          });
        },
        { root, threshold: Array.from({ length: 21 }, (_, i) => i / 20) }
      );
      teaseEls.forEach((el) => teaseObserver.observe(el));
      signal.addEventListener('abort', () => teaseObserver.disconnect());
    }
    root.querySelectorAll('[data-ph-tease-cta]').forEach((cta) => {
      cta.addEventListener(
        'click',
        () => {
          const panel = cta.closest('[data-ph-panel]');
          scrollToIndex(panels.indexOf(panel) + 1);
        },
        { signal }
      );
    });

    /*
     * Signup mini reminder (sections/signup.liquid's `.ph-signup__mini`),
     * ported from the prototype's own scroll check in updateScrollUi():
     * `position > panel's own offset + size` — once the visitor has
     * scrolled past a signup panel, its reminder slides in and stays until
     * either clicked (scrolls back to the form) or closed for the rest of
     * this visit. Each signup section gets its own independent reminder and
     * dismissed state, matching the source supporting more than one.
     */
    const signupMinis = Array.from(root.querySelectorAll('[data-ph-signup-mini]'));
    if (signupMinis.length) {
      const verticalQuery = matchMedia('(max-width: 989px)');
      const instances = signupMinis
        .map((mini) => {
          const panel = document.getElementById(mini.dataset.phSignupMini);
          if (!panel) return null;
          return { mini, panel, dismissed: false, shown: false };
        })
        .filter(Boolean);

      let signupMiniRaf = 0;

      function updateSignupMinis() {
        signupMiniRaf = 0;
        const vertical = verticalQuery.matches;
        const position = vertical ? root.scrollTop : root.scrollLeft;
        instances.forEach((instance) => {
          const { mini, panel, dismissed } = instance;
          const panelEnd = vertical
            ? panel.offsetTop + panel.offsetHeight
            : panel.offsetLeft + panel.offsetWidth;
          const visible = !dismissed && position > panelEnd;
          if (visible) instance.shown = true;
          if (instance.shown) mini.classList.add('ph-signup__mini--shown');
          mini.classList.toggle('ph-signup__mini--visible', visible);
        });
      }

      function requestSignupMinis() {
        if (signupMiniRaf) return;
        signupMiniRaf = requestAnimationFrame(updateSignupMinis);
      }

      instances.forEach((instance) => {
        const actionBtn = instance.mini.querySelector('[data-ph-signup-mini-action]');
        const closeBtn = instance.mini.querySelector('[data-ph-signup-mini-close]');
        actionBtn?.addEventListener(
          'click',
          () => scrollToIndex(panels.indexOf(instance.panel)),
          { signal }
        );
        closeBtn?.addEventListener(
          'click',
          () => {
            instance.dismissed = true;
            const mini = instance.mini;
            if (reducedMotion) {
              mini.classList.remove('ph-signup__mini--shown', 'ph-signup__mini--visible');
              return;
            }
            mini.classList.remove('ph-signup__mini--visible');
            mini.addEventListener(
              'animationend',
              () => mini.classList.remove('ph-signup__mini--shown'),
              { once: true, signal }
            );
          },
          { signal }
        );
      });

      updateSignupMinis();
      root.addEventListener('scroll', requestSignupMinis, { signal, passive: true });
      window.addEventListener('resize', requestSignupMinis, { signal });
      verticalQuery.addEventListener('change', requestSignupMinis, { signal });
      signal.addEventListener('abort', () => {
        if (signupMiniRaf) cancelAnimationFrame(signupMiniRaf);
      });
    }

    /*
     * Scroll progress bar — a thin bar fixed to the bottom of the viewport
     * that fills as the visitor moves through the showcase, on whichever axis
     * is scrolling (horizontal on desktop, vertical at max-width: 989px). Also
     * hints on landing that the page scrolls. Created here rather than in
     * markup so it only exists while the showcase is mounted; removed again on
     * cleanup (theme editor re-mounts). Styles: .ph-showcase-progress in
     * component-homepage-showcase.css.
     */
    {
      const progressQuery = matchMedia('(max-width: 989px)');
      const progress = document.createElement('div');
      progress.className = 'ph-showcase-progress';
      progress.setAttribute('aria-hidden', 'true');
      const progressFill = document.createElement('div');
      progressFill.className = 'ph-showcase-progress__fill';
      progress.append(progressFill);
      document.body.append(progress);

      let progressRaf = 0;

      function updateProgress() {
        progressRaf = 0;
        const vertical = progressQuery.matches;
        const position = vertical ? root.scrollTop : root.scrollLeft;
        const max = vertical ? root.scrollHeight - root.clientHeight : root.scrollWidth - root.clientWidth;
        const ratio = max > 0 ? Math.min(1, Math.max(0, position / max)) : 0;
        progress.hidden = max <= 0;
        progressFill.style.transform = `scaleX(${ratio})`;
      }

      function requestProgress() {
        if (progressRaf) return;
        progressRaf = requestAnimationFrame(updateProgress);
      }

      updateProgress();
      root.addEventListener('scroll', requestProgress, { signal, passive: true });
      window.addEventListener('resize', requestProgress, { signal });
      progressQuery.addEventListener('change', requestProgress, { signal });
      // Media loading in can change the scrollable length after mount.
      window.addEventListener('load', requestProgress, { signal, once: true });
      signal.addEventListener('abort', () => {
        if (progressRaf) cancelAnimationFrame(progressRaf);
        progress.remove();
      });
    }


    setActive(0);
    if (nav) nav.hidden = panels.length < 2;

    return () => {
      observer.disconnect();
      if (wheelRaf) cancelAnimationFrame(wheelRaf);
      controller.abort();
    };
  }

  function mount() {
    if (cleanup) {
      cleanup();
      cleanup = null;
    }
    const root = document.querySelector('[data-ph-showcase]');
    if (!root) return;
    cleanup = mountShowcase(root);
  }

  mount();

  // The theme editor can add, remove, or reorder homepage sections live;
  // re-scan the panel list whenever that happens so new sections
  // (story, shop-edit, signup, carousel) join the same scroll flow.
  document.addEventListener('shopify:section:load', mount);
  document.addEventListener('shopify:section:unload', mount);
  document.addEventListener('shopify:section:reorder', mount);
})();
